/**
 * Kaufschranke.
 *
 * Gratis bleibt Block 1: die Lichterbilder ansehen und drehen, dazu zehn Übungsfragen.
 * Alles andere gehört zum Kauf. Die Schranke ist eine Eigenschaft des Builds, nicht des
 * Nutzers: die Webfassung ist der freie SEO-Anker und hat sie aus, die App-Hülle hat sie
 * an. Deshalb steht sie in einer Umgebungsvariablen und nicht in einer Einstellung.
 *
 * Hier steht nur die Logik. Ob ein Kauf stattgefunden hat, weiß der Store, und die
 * Anbindung daran kommt mit der Hülle; bis dahin schaltet die Oberfläche frei und merkt
 * es sich auf dem Gerät.
 */
import type { Progress } from './progress'
import type { Settings } from './settings'

export type Section =
  | 'bestimmen'
  | 'buoys'
  | 'scenarios'
  | 'signals'
  | 'quiz'
  | 'cards'
  | 'exam'
  | 'progress'
  | 'settings'

/** Zehn Fragen, dann die Schranke. */
export const FREE_QUESTIONS = 10

/**
 * Preis der Vollversion in Euro. Vorläufig: der Preis ist eine Entscheidung von Matthias
 * (siehe LOOP.md, Human Gates), hier steht der Wert aus der Planung, damit die Oberfläche
 * ihn an einer Stelle hat.
 */
export const PRICE_EUR = 9.99

/**
 * Ist der Kauf an einen Store angebunden? Noch nicht: die Anbindung kommt mit der Hülle.
 * Solange das so ist, sagt die Kaufschranke das auch, statt einen Kauf vorzutäuschen.
 */
export const STORE_CONNECTED = false

/** Frei zugänglich, auch mit Schranke. */
const FREE_SECTIONS: Section[] = ['bestimmen', 'quiz', 'settings']

/**
 * Die Schranke kommt aus dem Build. Zusätzlich lässt sie sich über "?paywall=1"
 * einschalten, damit sich die Vollversion vorführen und prüfen lässt; ausschalten kann
 * die Adresse sie nicht, sonst wäre sie keine.
 */
export function paywallOn(
  env: Record<string, unknown> = import.meta.env as unknown as Record<string, unknown>,
  search = globalThis.location?.search ?? '',
): boolean {
  if (env.VITE_PAYWALL === '1') return true
  return new URLSearchParams(search).get('paywall') === '1'
}

/** Zahl der beantworteten Fragen, über alle Karten. */
export function questionsAnswered(progress: Progress): number {
  return Object.values(progress.cards).reduce((n, card) => n + card.attempts, 0)
}

export function questionsLeft(progress: Progress): number {
  return Math.max(0, FREE_QUESTIONS - questionsAnswered(progress))
}

/**
 * Ist dieser Bereich gesperrt? Das Quiz ist es erst, wenn die Gratisfragen aufgebraucht
 * sind, damit der Einstieg ohne Hürde funktioniert.
 */
export function locked(section: Section, settings: Settings, progress: Progress, on = paywallOn()): boolean {
  if (!on || settings.unlocked) return false
  if (!FREE_SECTIONS.includes(section)) return true
  return section === 'quiz' && questionsLeft(progress) === 0
}
